import { useState, useEffect } from "react";
import { useTheme } from "@/hooks/useTheme";

const navLinks = [
  { label: "Home", href: "#hero" },
  { label: "Solution", href: "#solution" },
  { label: "Industries", href: "#industries" },
  { label: "Case Studies", href: "#case-studies" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrolledBg = isDark
    ? "bg-[#0d0818]/95 backdrop-blur-sm border-b border-white/10"
    : "bg-white/95 backdrop-blur-sm border-b border-ivory-dark";

  const linkColor = scrolled
    ? isDark
      ? "text-violet-200 hover:text-white"
      : "text-violet-deep hover:text-violet-muted"
    : "text-white/90 hover:text-white";

  const hamburgerColor = scrolled
    ? isDark ? "text-violet-200" : "text-violet-deep"
    : "text-white";

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? scrolledBg : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-24 md:h-28">
        {/* Logo */}
        <a href="#hero" onClick={(e) => handleNavClick(e, "#hero")} className="flex items-center cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/28fc6c3a-0f66-4e71-a2ed-adf2d0f7d0ce.png"
            alt="Patronum AI Logo"
            className="object-contain"
            style={{ width: "90px", height: "90px" }}
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${linkColor}`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Right controls */}
        <div className="hidden md:flex items-center gap-3">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 ${
              scrolled
                ? isDark
                  ? "bg-white/10 hover:bg-white/20 text-white/80"
                  : "bg-gray-100 hover:bg-gray-200 text-violet-deep"
                : "bg-white/10 hover:bg-white/20 text-white/80"
            }`}
          >
            <i className={`${isDark ? "ri-sun-line" : "ri-moon-line"} text-base`}></i>
          </button>

          {/* CTA */}
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, "#contact")}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-violet-deep text-white hover:bg-violet-muted"
          >
            Book a Call
          </a>
        </div>

        {/* Mobile right */}
        <div className="md:hidden flex items-center gap-2">
          {/* Mobile Theme Toggle */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-all ${
              scrolled
                ? isDark ? "bg-white/10 text-white" : "bg-gray-100 text-violet-deep"
                : "bg-white/10 text-white"
            }`}
          >
            <i className={`${isDark ? "ri-sun-line" : "ri-moon-line"} text-sm`}></i>
          </button>
          {/* Hamburger */}
          <button
            className="flex items-center justify-center w-10 h-10 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl ${hamburgerColor}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          className={`md:hidden border-t px-6 py-4 flex flex-col gap-4 animate-fadeIn ${
            isDark ? "bg-[#0d0818] border-white/10" : "bg-white border-ivory-dark"
          }`}
        >
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className={`text-sm font-medium cursor-pointer ${
                isDark ? "text-violet-200 hover:text-white" : "text-violet-deep hover:text-violet-muted"
              }`}
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, "#contact")}
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold bg-violet-deep text-white cursor-pointer"
          >
            Book a Call
          </a>
        </div>
      )}
    </header>
  );
}
